import React from 'react'
import moment from 'moment'
import { isNil, startCase } from 'lodash'
import { IconSparkles, IconLock } from '@tabler/icons-react'
import pluralize from 'pluralize'

import { Button } from '@/components/buttons/Button'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { Pill } from '@/components/pills/Pill'
import {
  READY_FOR_REVIEW,
  IN_PROGRESS,
  COLLECTING_EVIDENCE,
  COMPLETED,
  IGNORED,
  TASK_STATUS_MAP,
} from '@/reducers/tasks/tasks.constants'
import { dateFormatMoment } from '@/utils/constants'
import { RUN_STATUS } from '@/reducers/promptimizer/promptimizer.redux'
import {
  CALIBRATION_RESULT,
  CALIBRATION_STOP,
} from '@/reducers/promptFeedback/promptFeedback.redux'

import '@/components/inbox/components/TaskCard.scss'

// AI activity in flight is `magical` (the design system's AI treatment); finished states keep the
// semantic outcome color. One label per state a person acts on differently, shared by the card,
// the drawer and the Calibration Agent tab so they never disagree.
export const calibrationState = (optimization, adopted = optimization?.adopted) => {
  if (isNil(optimization)) {
    return null
  }
  switch (optimization.status) {
    case RUN_STATUS.QUEUED:
      return { label: 'Queued for the agent', variant: 'magical' }
    case RUN_STATUS.RUNNING:
      return { label: 'Agent optimizing…', variant: 'magical' }
    case RUN_STATUS.FAILED:
      return { label: 'Agent run failed', variant: 'caution' }
    case RUN_STATUS.CANCELLED:
      return { label: 'Agent run stopped', variant: 'caution' }
    default:
      break
  }
  const { outcome } = optimization
  if (isNil(outcome)) {
    return null
  }
  if (outcome.result === CALIBRATION_RESULT.AUTO_APPLIED) {
    return { label: 'Applied automatically', variant: 'success' }
  }
  if (adopted) {
    return { label: 'Adopted', variant: 'success' }
  }
  if (outcome.result === CALIBRATION_RESULT.ALREADY_ADOPTED) {
    return { label: 'Already adopted', variant: 'success' }
  }
  if (outcome.stop_reason === CALIBRATION_STOP.ALREADY_SATISFIED) {
    return { label: 'No change needed', variant: 'success' }
  }
  if (outcome.stop_reason === CALIBRATION_STOP.SUCCESS) {
    return { label: 'Fix ready — awaiting apply', variant: 'success' }
  }
  return { label: 'Agent stuck — needs a human', variant: 'warning' }
}

// Shared with the Calibration Agent tab, so the card and the agenda can never disagree on a state.
export const OptimizationChip = ({ optimization, adopted, ...props }) => {
  const chip = calibrationState(optimization, adopted)
  if (!chip) {
    return null
  }
  return (
    <Pill xs {...{ [chip.variant]: true }} icon={<IconSparkles />} className="uppercase" {...props}>
      {chip.label}
    </Pill>
  )
}

export const PromptFeedbackCard = ({
  title,
  status,
  description,
  feedbackCount,
  createdDate,
  updatedDate,
  handleClick,
  buttonText,
  callType = 'call',
  orgName,
  scorecardName,
  isProtectedOrg = false,
  optimization,
  onCalibrate,
  calibrating = false,
  dataTestId,
}) => {
  const created = moment.utc(createdDate).local()
  const updated = moment.utc(updatedDate).local()
  const now = moment()
  const hasBeenUpdated = !created.isSame(updated)
  const dateToUse = hasBeenUpdated ? updated : created
  const displayDate =
    now.diff(dateToUse, 'hours') < 24
      ? dateToUse.fromNow()
      : dateToUse.format(dateFormatMoment.long)

  return (
    <div
      className="task-card"
      data-testid={dataTestId}
      onClick={isProtectedOrg ? undefined : handleClick}
    >
      <header>
        <h2>
          <IconSparkles className="status-magical icon-svg-large" />
          {title}
        </h2>
        <Pill
          xs
          success={status === COMPLETED}
          warning={status === READY_FOR_REVIEW}
          caution={status === IGNORED}
          emphasized={status === COLLECTING_EVIDENCE}
          informative={status === IN_PROGRESS}
          tooltip={
            status === COLLECTING_EVIDENCE
              ? 'Needs at least 10 reports to be ready for review'
              : undefined
          }
        >
          {TASK_STATUS_MAP[status]}
        </Pill>
        <OptimizationChip
          optimization={optimization}
          data-testid={`${dataTestId}-optimization-chip`}
        />
      </header>

      <p data-testid={`${dataTestId}-description`}>{description}</p>

      <footer>
        <div className="task-card__metadata">
          <InteractiveTooltip placement="top" content={dateToUse.format('MMMM Do YYYY, h:mm:ss a')}>
            <small>
              {hasBeenUpdated ? 'Last updated' : 'Created'} {displayDate}
            </small>
          </InteractiveTooltip>

          <div className="task-card__metadata--divider" />
          <Pill xs>{pluralize(startCase(`related ${callType}`), feedbackCount, true)}</Pill>

          {orgName && (
            <>
              <div className="task-card__metadata--divider" />
              <Pill xs>{orgName}</Pill>
            </>
          )}

          {scorecardName && (
            <>
              <div className="task-card__metadata--divider" />
              <Pill xs>{scorecardName}</Pill>
            </>
          )}

          {isProtectedOrg && (
            <>
              <div className="task-card__metadata--divider" />
              <InteractiveTooltip
                placement="top"
                content="Log into this organization to make edits"
              >
                <Pill xs caution>
                  <IconLock /> Protected
                </Pill>
              </InteractiveTooltip>
            </>
          )}
        </div>

        <div className="flex-align-center small-gap">
          {onCalibrate && (
            <Button
              icon={<IconSparkles className="status-magical" />}
              loading={calibrating}
              disabled={calibrating}
              onClick={(event) => {
                event.stopPropagation()
                onCalibrate()
              }}
              data-testid={`${dataTestId}-calibrate-button`}
            >
              Calibrate now
            </Button>
          )}
          {buttonText && !isProtectedOrg && (
            <Button data-testid="drawer-trigger-button">{buttonText}</Button>
          )}
        </div>
      </footer>
    </div>
  )
}
